import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppStore } from '../../../store/appStore';
import { useAppVersion } from '../../../hooks/useAppVersion';
import { isBrowserMode } from '../../browser-mode';
import { getChangesSinceVersion, type ChangelogEntry } from '../data/changelog';

type WhatsNewState = {
  isDialogOpen: boolean;
  newEntries: ChangelogEntry[];
  onDismiss: () => void;
};

export function useWhatsNew(): WhatsNewState {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newEntries, setNewEntries] = useState<ChangelogEntry[]>([]);
  const checkedRef = useRef(false);

  const lastSeenVersion = useAppStore((s) => s.lastSeenVersion);
  const setLastSeenVersion = useAppStore((s) => s.setLastSeenVersion);
  const currentVersion = useAppVersion();

  useEffect(() => {
    // Only check once
    if (checkedRef.current) return;

    // Wait for version to load
    if (!currentVersion) return;

    // Skip in browser mode and test mode
    if (isBrowserMode()) return;
    const skipInTest = localStorage.getItem('sql-studio-skip-session-dialog') === '1';
    if (skipInTest) return;

    checkedRef.current = true;

    // First install: silently set version, don't show dialog
    if (lastSeenVersion === null) {
      setLastSeenVersion(currentVersion);
      return;
    }

    // Check if there are new entries since last seen version
    const entries = getChangesSinceVersion(lastSeenVersion);
    if (entries.length > 0) {
      setNewEntries(entries);
      setIsDialogOpen(true);
    }
  }, [currentVersion, lastSeenVersion, setLastSeenVersion]);

  const onDismiss = useCallback(() => {
    setIsDialogOpen(false);
    if (currentVersion) {
      setLastSeenVersion(currentVersion);
    }
  }, [currentVersion, setLastSeenVersion]);

  return { isDialogOpen, newEntries, onDismiss };
}
